import { useCallback, useEffect, useState } from 'react';
import { createImpression, ImpressionApiError, type ImpressionPayload } from '../guestImpressionsApi';
import { MediaGallery } from '../media/MediaGallery';
import { MediaGalleryApiError, uploadImpressionFiles } from '../media/mediaGalleryApi';
import {
  fetchPublicationRestriction,
  publicationDecisionUrl,
  publicationRestrictionMessage,
  type PublicationRestriction,
} from '../publicationRestriction';
import { ImpressionEditor } from './ImpressionEditor';

type ImpressionFormScreenProps = { visitId: string; venueName: string; onDone: (message: string) => void; onCancel: () => void };

export function ImpressionFormScreen({ visitId, venueName, onDone, onCancel }: ImpressionFormScreenProps) {
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [flash, setFlash] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [restriction, setRestriction] = useState<PublicationRestriction | null>(null);
  const [restrictionChecking, setRestrictionChecking] = useState(true);

  const refreshRestriction = useCallback(async (): Promise<PublicationRestriction | null> => {
    setRestrictionChecking(true);
    try {
      const next = await fetchPublicationRestriction();
      setRestriction(next);
      const message = publicationRestrictionMessage(next);
      if (message) setFlash(message);
      return next;
    } catch {
      setFlash('Не удалось проверить возможность публикации. Попробуйте ещё раз — черновик останется на экране.');
      return null;
    } finally {
      setRestrictionChecking(false);
    }
  }, []);

  useEffect(() => {
    void refreshRestriction();
    const onFocus = () => { void refreshRestriction(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshRestriction]);

  const submit = async (payload: ImpressionPayload) => {
    setSubmitting(true);
    setFieldErrors({});
    setFlash(null);
    try {
      const currentRestriction = await fetchPublicationRestriction();
      setRestriction(currentRestriction);
      const blockedMessage = publicationRestrictionMessage(currentRestriction);
      if (blockedMessage) {
        setFlash(blockedMessage);
        return;
      }

      const created = await createImpression(visitId, payload);
      if (pendingFiles.length > 0) {
        try {
          await uploadImpressionFiles(pendingFiles, created.data.id);
        } catch (photoError) {
          if (photoError instanceof MediaGalleryApiError && (photoError.status === 403 || photoError.status === 503)) {
            onDone(`Впечатление сохранено. Фото не добавлены: ${photoError.message}`);
            return;
          }
          onDone('Впечатление сохранено, но часть фото не загрузилась');
          return;
        }
      }
      onDone('Впечатление сохранено');
    } catch (error) {
      if (error instanceof ImpressionApiError) {
        if (error.status === 409 && error.code === 'impression_already_exists') { onDone('Вы уже оставили впечатление'); return; }
        if (error.status === 403) {
          const refreshed = await refreshRestriction();
          setFlash(refreshed ? publicationRestrictionMessage(refreshed) ?? error.message : error.message);
          return;
        }
        if (error.status === 503) { setFlash('Проверка возможности публикации временно недоступна. Попробуйте позже — черновик останется на экране.'); return; }
        if (error.status === 422) { setFieldErrors(error.fields); setFlash('Проверьте заполненные поля'); return; }
        if (error.status === 429) { setFlash('Слишком много попыток, подождите минуту. Черновик сохранён.'); return; }
      }
      setFlash('Не удалось сохранить впечатление. Черновик остаётся на экране.');
    } finally {
      setSubmitting(false);
    }
  };

  const restrictionMessage = restriction ? publicationRestrictionMessage(restriction) : null;
  const restrictionDecisionUrl = restriction ? publicationDecisionUrl(restriction) : null;
  const mediaSlot = (
    <>
      {restrictionMessage && restrictionDecisionUrl && (
        <div className="impression-flash is-error" role="status">
          <strong>{restrictionMessage}</strong>{' '}
          <button type="button" className="impression-secondary-button" onClick={() => window.location.assign(restrictionDecisionUrl)}>
            Посмотреть решение
          </button>
        </div>
      )}
      <MediaGallery mode="impression_attach" entityId={visitId} maxItems={5} canEdit onPendingFilesChange={setPendingFiles} />
    </>
  );

  return (
    <ImpressionEditor
      title={`Ваше впечатление о ${venueName}`}
      submitLabel={restrictionMessage ? 'Публикация ограничена' : 'Отправить'}
      submitting={submitting}
      submitDisabled={restrictionChecking || Boolean(restrictionMessage)}
      fieldErrors={fieldErrors}
      screenFlash={flash}
      mediaSlot={mediaSlot}
      onSubmit={(payload) => void submit(payload)}
      onCancel={onCancel}
    />
  );
}
